import React from 'react';
import styled from '@emotion/styled';
import { useButton, useI18n } from '~Hooks';
import { Box, Button, RedButton } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import AddIcon from '~Assets/icons/add.svg';
import MinusIcon from '~Assets/icons/remove.svg';

const IconButton = styled(Button)`
  min-width: 0;
  flex: 0 0 auto;
  padding-left: 12px;
  padding-right: 12px;
`;

interface Props {
  /** Positive = add, negative = remove, zero = nothing chosen yet */
  pending: number;
  canIncrement: boolean;
  canDecrement: boolean;
  onIncrement: () => void;
  onDecrement: () => void;
  onConfirm: () => void;
  incrementDisabledToast?: string;
  decrementDisabledToast?: string;
  size?: 'small' | 'medium' | 'large' | 'xl';
}

/** [-] [Change / Add N / Remove N] [+] for the selected cheque line */
const QuantityStepper: React.FunctionComponent<Props> = ({
  pending,
  canIncrement,
  canDecrement,
  onIncrement,
  onDecrement,
  onConfirm,
  incrementDisabledToast,
  decrementDisabledToast,
  size = 'small',
}) => {
  const { i18n } = useI18n();

  const minusButton = useButton({
    onClick: onDecrement,
    disabled: !canDecrement,
    disabledToast: decrementDisabledToast,
  });

  const plusButton = useButton({
    onClick: onIncrement,
    disabled: !canIncrement,
    disabledToast: incrementDisabledToast,
  });

  const confirmButton = useButton({
    onClick: onConfirm,
    disabled: pending === 0,
  });

  const iconSize = size === 'small' ? '16px' : '20px';

  return (
    <Box display="flex" gap="xs" alignItems="stretch" fluid>
      <IconButton {...minusButton} size={size}>
        <Icon src={MinusIcon} color="var(--colorBlack)" width={iconSize} height={iconSize} />
      </IconButton>

      {pending < 0 ? (
        <RedButton {...confirmButton} fluid size={size}>
          {i18n('TM.RemoveCount', -pending)}
        </RedButton>
      ) : (
        <Button {...confirmButton} fluid size={size}>
          {pending > 0 ? i18n('TM.AddCount', pending) : i18n('TM.ChangeQuantity')}
        </Button>
      )}

      <IconButton {...plusButton} size={size}>
        <Icon src={AddIcon} color="var(--colorBlack)" width={iconSize} height={iconSize} />
      </IconButton>
    </Box>
  );
};

export default QuantityStepper;
